import { useEffect, useRef } from 'react';
import { Search, Palette, Code, Rocket, TrendingUp } from 'lucide-react';

const steps = [
  {
    number: '01',
    icon: Search,
    title: 'Analyse du besoin',
    description: 'Nous étudions vos objectifs et votre marché pour définir la meilleure stratégie.'
  },
  {
    number: '02',
    icon: Palette,
    title: 'Conception du design',
    description: 'Création de maquettes personnalisées selon votre identité de marque.'
  },
  {
    number: '03',
    icon: Code,
    title: 'Développement',
    description: 'Réalisation technique avec les technologies les plus performantes.'
  },
  {
    number: '04',
    icon: Rocket,
    title: 'Mise en ligne',
    description: 'Déploiement sécurisé et configuration optimale de votre site.'
  },
  {
    number: '05',
    icon: TrendingUp,
    title: 'Suivi et optimisation',
    description: 'Accompagnement continu et améliorations basées sur les données.'
  }
];

export const ProcessSection = () => {
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      },
      { threshold: 0.1 }
    );

    const items = sectionRef.current?.querySelectorAll('.fade-in');
    items?.forEach((item) => observer.observe(item));

    return () => observer.disconnect();
  }, []);

  return (
    <section 
      ref={sectionRef}
      data-testid="process-section"
      className="py-24 md:py-32 bg-[#1A1A1A]"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16 fade-in">
          <span className="inline-block text-xs font-medium text-[#1a365d] uppercase tracking-wider mb-4">
            Notre approche
          </span>
          <h2 
            data-testid="process-title"
            className="font-['Manrope'] text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4"
          >
            Notre méthode
          </h2>
          <p className="text-base text-white/60 max-w-2xl mx-auto">
            Un processus éprouvé pour garantir le succès de votre projet.
          </p>
        </div>

        {/* Process Steps */}
        <div className="relative">
          {/* Connecting line */}
          <div className="hidden lg:block absolute top-1/2 left-0 right-0 h-px bg-[#333333] -translate-y-1/2" />
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-8">
            {steps.map((step, index) => (
              <div
                key={step.number}
                data-testid={`process-step-${index}`}
                className={`fade-in relative stagger-${index + 1}`}
                style={{ animationDelay: `${index * 0.15}s` }}
              >
                {/* Number badge */}
                <div className="process-number w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-6 relative z-10">
                  <span className="text-white font-['Manrope'] font-bold text-sm">
                    {step.number}
                  </span>
                </div>
                
                {/* Content */}
                <div className="text-center">
                  <div className="w-14 h-14 bg-[#1A1A1A] rounded-xl flex items-center justify-center mx-auto mb-4 border border-[#333333]">
                    <step.icon className="text-[#4A90A4]" size={24} />
                  </div>
                  <h3 className="font-['Manrope'] text-lg font-bold text-white mb-2">
                    {step.title}
                  </h3>
                  <p className="text-sm text-white/50 leading-relaxed">
                    {step.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProcessSection;
